import { faCheck, faPen, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { faPen as faPenSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormError } from '@kcftech/design-system';
import { Button } from 'components/button/Button';
import { IButtonProps } from 'components/button/ButtonProps';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { Spinner } from 'components/spinner/Spinner';
import { UseSerializedInlineEditorOptions } from 'custom_hooks/useInlineEditor';
import { useOnClickOutside } from 'custom_hooks/useOnClickOutside';
import {
    CSSProperties,
    KeyboardEvent,
    MouseEvent,
    ReactNode,
    RefObject,
    useMemo,
    useRef,
} from 'react';
import styled from 'styled-components';
import { backgroundColorActive, disabledText } from 'styles/theme';
import { KCFColor } from 'styles/themes/KCFColor';

/**
 * Utility function for inline editors that do not need to convert types.
 */
export function noConvert<T, V = T>(value: T | undefined): V | undefined {
    return value !== undefined ? ((value as unknown) as V) : undefined;
}

/**
 * Core props for an inline editor.
 */
export type BaseInlineEditorProps<TValue, TInput> = Pick<
    UseSerializedInlineEditorOptions<TValue, TInput>,
    'value' | 'validate' | 'onConfirm' | 'onClose'
> &
    Pick<
        InlineEditorProps,
        | 'isDisabled'
        | 'isPending'
        | 'editorClass'
        | 'editorStyle'
        | 'valueClass'
        | 'valueStyle'
        | 'containerClass'
        | 'ariaLabel'
    > &
    Readonly<{
        /**
         * How to render the current or pending value when not editing.
         */
        renderValue?: (value: TValue | undefined) => ReactNode;
    }>;

export type InlineEditorProps = Readonly<{
    /**
     * Content when not editing.
     */
    value: ReactNode;
    /**
     * Content when editing.
     */
    editor: ReactNode;
    /**
     * When true, editor is visible.
     */
    isEditing: boolean;
    /**
     * Error to display.
     */
    error: string | undefined;
    /**
     * When true, the pencil icon is no longer visible and the editor cannot be opened.
     * Differs from `canEdit` which just disables the edit button.
     * Defaults to `false`.
     */
    isDisabled?: boolean;
    /**
     * Is the request still pending.
     * Not always applicable as some inline editors don't fire the update on confirm (such
     * as those inside modals or other forms).
     */
    isPending?: boolean;
    /**
     * When true the edit button is enabled (only when in edit mode).
     */
    canEdit: boolean;
    /**
     * When true the close button is enabled and clicking outside of the
     * editor can close the editor (only when in edit mode).
     */
    canClose: boolean;
    /**
     * When true the confirm button is enabled (only when in edit mode).
     */
    canConfirm: boolean;
    /**
     * A function to call when the confirm button is clicked.
     */
    onConfirm: () => void;
    /**
     * A function to call when the edit button is clicked.
     */
    onEdit: () => void;
    /**
     * A function to call when the cancel button is clicked.
     */
    onClose: () => void;
    /**
     * Additional content elements to be considered when determining if the user
     * has clicked outside of the inline editor.
     */
    contentRefs?: RefObject<HTMLElement>[];
    /**
     * Optional class to apply to editor.
     */
    editorClass?: string;
    /**
     * Optional styles to apply to editor.
     */
    editorStyle?: CSSProperties;
    /**
     * Optional styles to apply to value.
     */
    valueStyle?: CSSProperties;
    /**
     * Optional class to apply to the value.
     */
    valueClass?: string;
    /**
     * Optional class to apply to the overall container.
     */
    containerClass?: string;
    /**
     * Flag to disable "Close on click outside" functionality.
     */
    disableCloseOnClickOutside?: boolean;
    /** aria-label to apply to the top div of the inline editor */
    ariaLabel?: string;
}>;

const IconButton = styled(Button)<IButtonProps & { $color: string }>`
    font-size: 12px;
    padding: 0.25rem 0.75rem;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    color: ${({ disabled, $color }) => (disabled ? disabledText : $color)};
    border-radius: 1rem;
    border: 1px solid transparent;
    transition: none;
    &:hover:enabled,
    &:focus:enabled {
        background-color: ${backgroundColorActive};
    }
    &:active:enabled {
        border-color: ${KCFColor.defaultButton}88;
    }
`;

const StyledHoverIconButton = styled(HoverIconButton)`
    font-size: 1.5rem;
`;

const ButtonGroup = styled.div`
    width: 5rem;
    display: flex;
    flex-direction: row;
    justify-content: center;
`;

export function InlineEditor({
    value,
    editor,
    isEditing,
    error,
    isDisabled = false,
    isPending,
    canEdit,
    canClose,
    canConfirm,
    onConfirm,
    onEdit,
    onClose,
    contentRefs,
    editorClass,
    editorStyle,
    valueStyle,
    valueClass,
    containerClass,
    ariaLabel,
    disableCloseOnClickOutside = false,
}: InlineEditorProps) {
    const editorRef = useRef<HTMLDivElement>(null);
    const allContentRefs = useMemo(
        () => (contentRefs ? [editorRef, ...contentRefs] : editorRef),
        [contentRefs]
    );

    useOnClickOutside(
        allContentRefs,
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
        },
        !disableCloseOnClickOutside && isEditing && canClose
    );

    const onEditClick = (event: MouseEvent | KeyboardEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onEdit();
    };

    const onConfirmClick = (event: MouseEvent | KeyboardEvent) => {
        if (isEditing && canConfirm) {
            event.preventDefault();
            event.stopPropagation();
            onConfirm();
        }
    };

    const onCancelClick = (event: MouseEvent | KeyboardEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onClose();
    };

    const onContainerClick = (event: MouseEvent | KeyboardEvent) => {
        if (isEditing) {
            event.stopPropagation();
        }
    };

    const renderButtons = () => {
        if (isPending) {
            return <Spinner size="1x" />;
        }
        if (isDisabled) {
            return undefined;
        }
        if (isEditing) {
            return (
                <>
                    <IconButton
                        disabled={!canConfirm}
                        $color={KCFColor.confirmGreen}
                        onClick={onConfirmClick}
                        aria-label="confirm"
                    >
                        <FontAwesomeIcon icon={faCheck} />
                    </IconButton>
                    <IconButton
                        disabled={!canClose}
                        $color={KCFColor.red1}
                        onClick={onCancelClick}
                        aria-label="cancel"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                </>
            );
        }
        return (
            <StyledHoverIconButton
                title="Edit"
                disabled={!canEdit}
                icon={faPen}
                hoverIcon={faPenSolid}
                color={KCFColor.blue5}
                onClick={onEditClick}
                aria-label="open editor"
                className="f-15"
            />
        );
    };

    return (
        /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
        <div
            aria-label={ariaLabel}
            ref={editorRef}
            className={`flex-row align-center ${containerClass ?? ''}`}
            onClick={onContainerClick}
        >
            {isEditing && !isDisabled ? (
                <div className="flex-grow-1">
                    <div className={editorClass} style={editorStyle}>
                        {editor}
                    </div>
                    <div className="text-nowrap">
                        <FormError message={error} />
                    </div>
                </div>
            ) : (
                <div className={`flex-grow-1 ${valueClass ?? ''}`} style={valueStyle}>
                    {value}
                </div>
            )}
            <ButtonGroup>{renderButtons()}</ButtonGroup>
        </div>
    );
}
